import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radar, ShieldCheck, Info } from "lucide-react";
import { siMethodology } from "@/lib/api";

/** Public transparent methodology page — spec §10.
 *  Every score's inputs, weights, formula and limitations are disclosed. */
export default function SearchIntelligenceMethodology() {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    siMethodology().then(setDoc).catch(e => setErr(e.message || "Failed to load."));
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="si-methodology">
      <Navbar />
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Radar className="h-4 w-4" /> Methodology · v{doc?.version || "…"}
          </div>
          <h1 className="mt-3 font-heading text-4xl font-black text-slate-900 sm:text-5xl">
            No opaque score. No black box.
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600">
            {doc?.headline || "How WebsiteTech Search Intelligence turns crawl data into four transparent scores."}
          </p>
        </div>
      </section>

      {err && <div className="mx-auto max-w-4xl p-6 text-sm text-rose-600">{err}</div>}

      {doc && (
        <>
          <section className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Guiding principles</h2>
            <ul className="mt-4 space-y-3">
              {doc.principles.map((p, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="text-slate-700">{p}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mx-auto max-w-4xl px-4 py-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900">The four scores</h2>
            <div className="mt-4 space-y-6">
              {doc.scores.map((s) => (
                <Card key={s.key} className="border-slate-200 bg-white p-6" data-testid={`si-method-${s.key}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-heading text-xl font-bold text-slate-900">{s.label}</h3>
                    <Badge variant="outline">{s.range}</Badge>
                  </div>
                  <p className="mt-2 text-slate-700">{s.definition}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Inputs</div>
                      <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
                        {s.inputs.map((i, idx) => <li key={idx}>{i}</li>)}
                      </ul>
                    </div>
                    {s.weights && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Weights</div>
                        <ul className="mt-2 space-y-1 font-mono text-xs text-slate-700">
                          {Object.entries(s.weights).map(([k, v]) => (
                            <li key={k}>{k}: <b>{v}</b></li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {s.note && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                      <Info className="mt-0.5 h-4 w-4" /><span>{s.note}</span>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </section>

          <section className="mx-auto max-w-4xl px-4 py-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Readiness formula</h2>
            <pre className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-slate-900 p-4 font-mono text-sm text-emerald-300" data-testid="si-method-formula">
{doc.readiness_formula}
            </pre>
          </section>

          <section className="mx-auto max-w-4xl px-4 py-6 pb-16">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Known limitations</h2>
            <ul className="mt-4 space-y-2 text-slate-700">
              {doc.limitations.map((l, i) => (
                <li key={i} className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{l}</li>
              ))}
            </ul>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
